/**
 * usePools Hook - Phase 2C-2
 * 
 * Fetches all pools with optional filtering.
 * Uses existing PoolService pattern.
 */

import { useState, useEffect, useCallback } from 'react';
import { Pool, PoolFilters } from '../types';
import { PoolService, ApiError } from '../api';

interface UsePoolsResult {
  pools: Pool[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function usePools(filters?: PoolFilters): UsePoolsResult {
  const [pools, setPools] = useState<Pool[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPools = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await PoolService.getAll();
      
      // Apply client-side filters if provided
      let filtered = data;
      if (filters) {
        if (filters.domain) {
          filtered = filtered.filter(p => p.domain === filters.domain);
        }
        if (filters.status) {
          filtered = filtered.filter(p => p.status === filters.status);
        }
        if (filters.mode) {
          filtered = filtered.filter(p => p.mode === filters.mode);
        }
      }
      
      setPools(filtered);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load pools');
    } finally {
      setLoading(false);
    }
  }, [filters?.domain, filters?.status, filters?.mode]);

  useEffect(() => {
    fetchPools();
  }, [fetchPools]);

  return { pools, loading, error, refetch: fetchPools };
}
